import { useEffect, useMemo } from "react"
import {
  Box,
  InputAdornment,
  InputBase,
  useTheme,
  ToggleButtonGroup,
} from "@mui/material"
import { deepmerge } from "@mui/utils"
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace"
import StarIcon from "@mui/icons-material/Star"

import _ from "lodash"
import { getFeatureName } from "utils/feature"
import { getIcon } from "utils/icon"

import { useTranslation } from "providers/i18n"

import { SearchIcon, CloseIcon as CrossIcon } from "components/icons"
import { SearchBasePopup } from "components/domains/search/SearchBasePopup"
import { SearchResult } from "components/domains/search/SearchResult"
import { HorizontalScrollPane } from "components/HorizontalScrollPane"
import { CategoryFilterChip } from "components/CategoryFilterBar"
import { AdSuggestList } from "components/domains/ads"
import { SearchKeyboard } from "./SearchKeyboard"
import { CloseIconButton } from "components/Button"

export const SearchPopupContent = ({
  openSearchState,
  onClose,
  popupType,
  onChange,
  onChangeKeyboard,
  onSelectResult,
  onClickSuggestedItem,
  searchResultItems,
  searchInputRef,
  keyboardRef,
  showOnscreenKeyboard,
  searchValue,
  inputProps,
  closeButtonProps,
  closeIconProps,
  searchBasePopupProps = {},
  closeIcon: CloseIcon = KeyboardBackspaceIcon,
  searchKeyboardProps = {},
  searchResultProps,
  onClickClearSearchValue,
  categories = [],
  suggestedListItems = [],
  onClickCategory = (e) => {},
  venueFilterComponent = null,
}) => {
  const { sx: closeButtonSxProps } = closeButtonProps || {}
  const { sx: closeIconSxProps } = closeIconProps || {}
  const { sx: inputSxProps } = inputProps || {}
  const { t } = useTranslation({ defaultValue: null })
  // Theme & Styling
  const theme = useTheme()
  const searchBarStyles =
    theme.components[`VuiSearchPopupContent`]?.styleOverrides || {}
  const categoryQuickSearchStyles =
    theme.components[`CategoryQuickSearch`]?.styleOverrides || {}
  const searchIcon = useMemo(
    () => getIcon(theme?.assets?.searchIcon) || <SearchIcon />,
    [theme]
  )

  // TODO: Implement feature flag support later
  const { multipleLocationOption = true } =
    theme.components[`VuiSearchPopupContent`]?.defaultProps || {}

  const { hasNextPage, endScrollRef, ...restSearchResultProps } =
    searchResultProps || {}

  const closeIcon = useMemo(() => {
    const iconSx = deepmerge(searchBarStyles.closeButtonIcon, closeIconSxProps)
    return (
      getIcon(theme?.assets?.closeIcon, {
        ...closeIconProps,
        sx: iconSx,
      }) || <CloseIcon {...closeIconProps} sx={iconSx} />
    )
  }, [
    theme,
    closeIconProps,
    closeIconSxProps,
    searchBarStyles.closeButtonIcon,
    CloseIcon,
  ])

  // Group Search Result by name.en if duplicate name store in locations property
  const uniqueByNameResults = useMemo(() => {
    return (
      _(searchResultItems)
        //amenity doesn't have "name.en" but "name"
        .groupBy(
          (item) =>
            (item.feature_type === "occupant" ? item.name.en : item.id) &&
            `${_.get(item, "properties.name.en")}-${_.get(item, "isAd")}`
        )
        .map((locations) => {
          return locations.length > 1
            ? {
                ...locations[0],
                hasMultipleLocations: locations.length > 1,
                locations,
              }
            : locations[0]
        })
        .value()
    )
  }, [searchResultItems])

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus()
    }
    // eslint-disable-next-line
  }, [searchInputRef.current])

  return (
    <SearchBasePopup
      {...searchBasePopupProps}
      open={openSearchState}
      onClose={onClose}
      type={popupType}
    >
      {/* SearchBar */}
      <Box
        sx={searchBarStyles.root}
        className={`popupType-${popupType} VuiSearchPopupContent-searchBar`}
      >
        <CloseIconButton
          {...closeButtonProps}
          icon={closeIcon}
          className="VuiSearchPopupContent-closeButton"
          sx={deepmerge(searchBarStyles.closeButton, closeButtonSxProps)}
          onClick={onClose}
        />
        <InputBase
          className={`popupType-${popupType} VuiSearchPopupContent-searchBarInput`}
          fullWidth
          placeholder={t("Search")}
          onChange={onChange}
          inputRef={searchInputRef}
          defaultValue={searchValue}
          startAdornment={
            <InputAdornment position="start">{searchIcon}</InputAdornment>
          }
          endAdornment={
            <InputAdornment position="end">
              <CrossIcon onClick={onClickClearSearchValue} />
            </InputAdornment>
          }
          {...inputProps}
          sx={deepmerge(searchBarStyles.searchInput, inputSxProps)}
        />
        <Box
          className="VuiSearchPopupContent-venueFilterContainer"
          sx={searchBarStyles.venueFilterContainer}
        >
          {venueFilterComponent}
        </Box>
      </Box>
      {/* End of SearchBar */}

      {/* SearchResult + Suggested Brand*/}
      <SearchResult
        {...searchResultProps}
        startAdornment={
          suggestedListItems.length > 0 &&
          !searchValue && (
            <AdSuggestList
              sx={searchBarStyles.suggestedList}
              suggestedListItems={suggestedListItems}
              onClickSuggestedItem={onClickSuggestedItem}
            />
          )
        }
        popupType={popupType}
        items={multipleLocationOption ? uniqueByNameResults : searchResultItems}
        onSelect={onSelectResult}
        hasNextPage={hasNextPage}
        endScrollRef={endScrollRef}
        {...restSearchResultProps}
      />
      {/* End of SearchResult + Sugguested Brand */}

      {/* Category Quick Search */}
      <Box
        sx={categoryQuickSearchStyles.categoryFilterBarContainer}
        className={`popupType-${popupType} VuiSearchPopupContent-categoryFilterBar`}
      >
        <HorizontalScrollPane
          stackProps={{
            sx: categoryQuickSearchStyles.scrollPane,
          }}
        >
          <ToggleButtonGroup
            sx={categoryQuickSearchStyles.categoryFilterBar}
            exclusive
            //TODO: check if this variant still needed
            //@ts-ignore
            variant="borderSeperate"
          >
            {categories.map((category) => (
              <CategoryFilterChip
                className={`${
                  category?.properties?.is_featured ? "highlight" : ""
                }`}
                value={category.id}
                key={`category-filter-${category.id}`}
                Icon={
                  category?.properties?.is_featured ? (
                    <StarIcon />
                  ) : (
                    getIcon(category.properties)
                  )
                }
                onClick={() => onClickCategory(category)}
                sx={categoryQuickSearchStyles.categoryFilterChip}
              >
                {t(getFeatureName(category))}
              </CategoryFilterChip>
            ))}
          </ToggleButtonGroup>
        </HorizontalScrollPane>
      </Box>
      {/* End of Category Quick Search */}

      {showOnscreenKeyboard && (
        <SearchKeyboard
          {...searchKeyboardProps}
          keyboardRef={keyboardRef}
          value={searchValue}
          onChange={onChangeKeyboard}
        />
      )}
      <CloseIconButton
        {...closeButtonProps}
        icon={closeIcon}
        className="VuiSearchPopupContent-closeButton--bottom"
        sx={deepmerge(searchBarStyles.closeButton, closeButtonSxProps)}
        onClick={onClose}
      />
    </SearchBasePopup>
  )
}
